import {CircularProgress, TableContainer, TableHead, TableBody, TableCell, TableRow, Paper, Table, Grid, Tabs, Tab} from '@mui/material';
import {useTranslation} from "react-i18next";
import React, {useState} from 'react';
import {observer} from 'mobx-react';
import {monthNames, getCityName, getCountryName} from '../Trans.js';
import {TAB_LOCATION, TAB_CITY, TAB_LOC} from './StatsCommon.js';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import StatsCityMap from './StatsCityMap.js';
import StatsFilter from './StatsFilter.js';
import {COMM_OK} from '../WsR2.js';

/* Tab */
const CT_LIST		= 0;
export const CT_MAP	= 1;
const CT_BY_MONTH	= 2;

/* Sorting */
const CSO_N_OBS		= 0;
const CSO_N_IND		= 1;
const CSO_N_SPEC	= 2;
const CSO_CITY		= 3;
const CSO_OBS_PER_SPEC	= 4;

const CSD_DESC		= 0;
const CSD_ASC		= 1;

const StatsCityByMonth = observer((props) => {
	const [loading, setLoading] = useState(false);
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	let tot_num = 0;

	function cityStatsMonthRxWs(status, msg) {
		setLoading(false);

		if (status !== COMM_OK) {
			alert('cityStatsMonthRxWs: error ' + status);
			return;
		} else if (msg.result !== 'OK') {
			alert('cityStatsMonthRxWs: ' + msg.data.desc + '!');
			return;
		}

		if (gStorage.stats.tab !== TAB_LOCATION || gStorage.stats.loc_tab !== TAB_CITY || gStorage.stats.city_tab !== CT_BY_MONTH)
			return;

		// console.log('cityStatsMonthRxWs', msg);
		gStorage.stats.setObsList(msg.data.list);
	}
	
	function loadCityMonthStats() {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'location',
			op: 'citybymonth',
			data: {
				country: gStorage.stats.country.id,
				year: gStorage.stats.statsYear,
				user: /*gStorage.stats.allUsers ? 0 : */gStorage.conn.user.id,
			}
		};

		// console.log('loadCityMonthStats', msg);
		setLoading(true);
		gStorage.conn.ws.send(msg, cityStatsMonthRxWs);
	}

	if (gStorage.stats.city_tab !== CT_BY_MONTH)
		return null;

	if (loading) {
		return (
			<Grid direction="column" justifyContent="center" alignItems="center" spacing={0}>
				<CircularProgress sx={{mt: 10}} enableTrackSlot/>
			</Grid>
		);
	}

	if (!gStorage.stats.obsList) {
		if (gStorage.conn.online)
			loadCityMonthStats();

		return null;
	}

	return (
		<TableContainer component={Paper} elevation={0} style={{margin: 2}}>
			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell>{t('stats.month')}</TableCell>
						<TableCell style={{textAlign: 'right'}}>{t('stats.count')}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{gStorage.stats.obsList.map((ms, ind) => {
						tot_num += ms;

						return (
							<TableRow key={ind}>
								<TableCell>
									{t(monthNames[ind])}
								</TableCell>
								<TableCell style={{textAlign: 'right'}}>
									{ms}
								</TableCell>
							</TableRow>
						);
					})}

					<TableRow key={12}>
						<TableCell>
						</TableCell>
						<TableCell style={{textAlign: 'right', fontWeight: 'bold'}}>
							{tot_num}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	)
});

const StatsCityList = observer((props) => {
	let cnt_sum, num_sum, num_spec, obs_per_spec_sum;
	const [loading, setLoading] = useState(false);
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	function statsCityGetCityName(id) {
		const city = gStorage.lists.citylist.list.find(elem => elem.id === id);
		const usr = gStorage.conn.user;

		// console.log('statsCityGetCityName', id, city);

		let a_str = '';

		if (city.country !== usr.def_country) {
			const country = gStorage.lists.countrylist.find(x => x.id === city.country);
			a_str = getCountryName(country, usr) + ', ';
		}

		return a_str += getCityName(city, gStorage.conn.user);
	}

	function sortByNobs(p1, p2) {
		if (gStorage.stats.city_sort_dir === CSD_ASC)
			return p1.cnt - p2.cnt;

		return p2.cnt - p1.cnt;
	}

	function sortByNind(p1, p2) {
		if (gStorage.stats.city_sort_dir === CSD_ASC)
			return p1.num - p2.num;

		return p2.num - p1.num;
	}

	function sortByNspec(p1, p2) {
		if (gStorage.stats.city_sort_dir === CSD_ASC)
			return p1.n_spec - p2.n_spec;

		return p2.n_spec - p1.n_spec;
	}

	function sortByCityName(p1, p2) {
		if (gStorage.stats.city_sort_dir === 1)
			return statsCityGetCityName(p1.id).localeCompare(statsCityGetCityName(p2.id), 'fi');
		
		return statsCityGetCityName(p2.id).localeCompare(statsCityGetCityName(p1.id), 'fi');
	}

	function sortByObsPerSpec(p1, p2) {
		const p1_obs_per_spec = p1.cnt / p1.n_spec;
		const p2_obs_per_spec = p2.cnt / p2.n_spec;

		if (gStorage.stats.city_sort_dir === 1)
			return p1_obs_per_spec - p2_obs_per_spec;

		return p2_obs_per_spec - p1_obs_per_spec;
	}

	function cityStatsSortResults(list) {
		// console.log('cityStatsSortResults', gStorage.stats.city_sort_order, gStorage.stats.city_sort_dir);

		switch (gStorage.stats.city_sort_order) {
		case CSO_N_OBS:
			return list.toSorted(sortByNobs);
		case CSO_N_IND:
			return list.toSorted(sortByNind);
		case CSO_N_SPEC:
			return list.toSorted(sortByNspec);
		case CSO_CITY:
			return list.toSorted(sortByCityName);
		case CSO_OBS_PER_SPEC:
			return list.toSorted(sortByObsPerSpec);
		default:
			console.error('Invalid city stats sorting order', 
				gStorage.stats.city_sort_order);
			break;
		}

		return null;
	}

	function cityStatsRxWs(status, msg) {
		setLoading(false);

		if (status !== COMM_OK) {
			alert('cityStatsRxWs: error ' + status);
			return;
		} else if (msg.result !== 'OK') {
			alert('cityStatsRxWs: ' + msg.data.desc + '!');
			return;
		}

		if (gStorage.stats.tab !== TAB_LOCATION || gStorage.stats.loc_tab !== TAB_CITY || gStorage.stats.city_tab !== CT_LIST)
			return;

		// console.log('cityStatsRxWs', msg);
		gStorage.stats.setObsList(cityStatsSortResults(msg.data.list));
	}

	function loadCityStats() {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'location',
			op: 'cities',
			data: {
				country: gStorage.stats.country.id,
				year: gStorage.stats.statsYear,
				user: gStorage.conn.user.id,
			}
		};

		// console.log('loadCityStats', msg);
		setLoading(true);
		gStorage.conn.ws.send(msg, cityStatsRxWs);
	}

	function citySignCityName() {
		if (gStorage.stats.city_sort_order !== CSO_CITY)
			return '';

		return gStorage.stats.city_sort_dir === CSD_DESC ? ' ↓' : ' ↑' ;
	}

	function citySignNumSpec() {
		if (gStorage.stats.city_sort_order !== CSO_N_SPEC)
			return '';

		return gStorage.stats.city_sort_dir === CSD_DESC ? ' ↓' : ' ↑' ;
	}

	function citySignNumObs() {
		if (gStorage.stats.city_sort_order !== CSO_N_OBS)
			return '';

		return gStorage.stats.city_sort_dir === CSD_DESC ? ' ↓' : ' ↑';
	}

	function citySignNumInd() {
		if (gStorage.stats.city_sort_order !== CSO_N_IND)
			return '';

		return gStorage.stats.city_sort_dir === CSD_DESC ? ' ↓' : ' ↑';
	}

	function citySignObsPerSpec() {
		if (gStorage.stats.city_sort_order !== CSO_OBS_PER_SPEC)
			return '';

		return gStorage.stats.city_sort_dir === CSD_DESC ? ' ↓' : ' ↑';
	}

	function tglSortDir() {
		return gStorage.stats.city_sort_dir === CSD_DESC ? CSD_ASC : CSD_DESC;
	}
	
	function setSortByCityName() {
		if (gStorage.stats.city_sort_order === CSO_CITY) {
			gStorage.stats.setCityOrder(CSO_CITY, tglSortDir());
		} else {
			gStorage.stats.setCityOrder(CSO_CITY, CSD_ASC);
		}

		gStorage.stats.setObsList(cityStatsSortResults(gStorage.stats.obsList));
	}

	function setSortByNumSpec() {
		if (gStorage.stats.city_sort_order === CSO_N_SPEC) {
			gStorage.stats.setCityOrder(CSO_N_SPEC, tglSortDir());
		} else {
			gStorage.stats.setCityOrder(CSO_N_SPEC, CSD_DESC);
		}

		gStorage.stats.setObsList(cityStatsSortResults(gStorage.stats.obsList));
	}

	function setSortByNumObs() {
		if (gStorage.stats.city_sort_order === CSO_N_OBS) {
			gStorage.stats.setCityOrder(CSO_N_OBS, tglSortDir());
		} else {
			gStorage.stats.setCityOrder(CSO_N_OBS, CSD_DESC);
		}

		gStorage.stats.setObsList(cityStatsSortResults(gStorage.stats.obsList));
	}

	function setSortByNumInd() {
		if (gStorage.stats.city_sort_order === CSO_N_IND) {
			gStorage.stats.setCityOrder(CSO_N_IND, tglSortDir());
		} else {
			gStorage.stats.setCityOrder(CSO_N_IND, CSD_DESC);
		}

		gStorage.stats.setObsList(cityStatsSortResults(gStorage.stats.obsList));
	}

	function setSortByObsPerSpec() {
		if (gStorage.stats.city_sort_order === CSO_OBS_PER_SPEC) {
			gStorage.stats.setCityOrder(CSO_OBS_PER_SPEC, tglSortDir());
		} else {
			gStorage.stats.setCityOrder(CSO_OBS_PER_SPEC, CSD_DESC);
		}

		gStorage.stats.setObsList(cityStatsSortResults(gStorage.stats.obsList));
	}

	function enterCityDetails(c_stats) {
		/* console.log('enterCityDetails, city:', c_stats.id); */

		gStorage.stats.setObsList(null);
		gStorage.stats.setTab(TAB_LOCATION);
		gStorage.stats.setLocTab(TAB_LOC);
		gStorage.stats.setLocation(-c_stats.id);
	}

	if (gStorage.stats.city_tab !== CT_LIST)
		return null;

	if (loading) {
		return (
			<Grid direction="column" justifyContent="center" alignItems="center" spacing={0}>
				<CircularProgress sx={{mt: 10}} enableTrackSlot/>
			</Grid>
		)
	}

	if (!gStorage.stats.obsList) {
		if (gStorage.conn.online) {
			gStorage.stats.setCityOrder(CSO_N_SPEC, CSD_DESC);
			loadCityStats();
		}

		return null;
	}

	cnt_sum = 0;
	num_sum = 0;
	num_spec = 0;
	obs_per_spec_sum = 0.0;

	return (
		<TableContainer component={Paper} elevation={0} style={{margin: 2}} sx={{paddingTop: 2}}>
			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell align='center' padding='checkbox'>#</TableCell>
						<TableCell align='left'  padding='checkbox' sx={{cursor: 'pointer'}} onClick={setSortByCityName}>{t('stats.city') + citySignCityName()}</TableCell>
						<TableCell align='right' padding='checkbox' sx={{cursor: 'pointer'}} onClick={setSortByNumSpec}>{t('stats.num_species') + citySignNumSpec()}</TableCell>
						<TableCell align='right' padding='checkbox' sx={{cursor: 'pointer'}} onClick={setSortByNumObs}>{t('taxons.count2') + citySignNumObs()}</TableCell>
						<TableCell align='right' padding='checkbox' sx={{cursor: 'pointer'}} onClick={setSortByObsPerSpec}>{t('stats.obs_per_spec') + citySignObsPerSpec()}</TableCell>
						<TableCell align='right' padding='checkbox' sx={{cursor: 'pointer'}} onClick={setSortByNumInd}>{t('taxons.num2') + citySignNumInd()}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{gStorage.stats.obsList && gStorage.stats.obsList.map((c, i) => {
						const obs_per_spec = (c.cnt / c.n_spec);

						cnt_sum += c.cnt;
						num_sum += c.num;
						num_spec += c.n_spec;
						obs_per_spec_sum += obs_per_spec;

						return (
							<TableRow key={i + 1}>
								<TableCell align='center' padding='checkbox'>{i + 1}</TableCell>
								<TableCell sx={{cursor: 'pointer'}} onClick={enterCityDetails.bind(this, c)}>{statsCityGetCityName(c.id)}</TableCell>
								<TableCell align='right' padding='checkbox'>{c.n_spec}</TableCell>
								<TableCell align='right' padding='checkbox'>{c.cnt}</TableCell>
								<TableCell align='right' padding='checkbox'>{obs_per_spec.toFixed(2)}</TableCell>
								<TableCell align='right' padding='checkbox'>{c.num}</TableCell>
							</TableRow>);
					})}

					{gStorage.stats.obsList && gStorage.stats.obsList.length ? 
						<>
							<TableRow key={gStorage.stats.obsList.length + 1}>
								<TableCell align='center' padding='checkbox'></TableCell>
								<TableCell></TableCell>
								<TableCell style={{fontWeight: 'bold'}} align='right' padding='checkbox'>{num_spec}</TableCell>
								<TableCell style={{fontWeight: 'bold'}} align='right' padding='checkbox'>{cnt_sum}</TableCell>
								<TableCell style={{fontWeight: 'bold'}} align='right' padding='checkbox'>{(obs_per_spec_sum / gStorage.stats.obsList.length).toFixed(2)}</TableCell>
								<TableCell style={{fontWeight: 'bold'}} align='right' padding='checkbox'>{num_sum}</TableCell>
							</TableRow> 
							<TableRow key={gStorage.stats.obsList.length + 2}>
								<TableCell align='center' padding='checkbox'></TableCell>
								<TableCell></TableCell>
								<TableCell style={{fontWeight: 'bold'}} align='right' padding='checkbox'>{(num_spec / gStorage.stats.obsList.length).toFixed(2)}</TableCell>
								<TableCell style={{fontWeight: 'bold'}} align='right' padding='checkbox'>{(cnt_sum / gStorage.stats.obsList.length).toFixed(2)}</TableCell>
								<TableCell style={{fontWeight: 'bold'}} align='right' padding='checkbox'></TableCell>
								<TableCell style={{fontWeight: 'bold'}} align='right' padding='checkbox'>{(num_sum / gStorage.stats.obsList.length).toFixed(2)}</TableCell>
							</TableRow> 
						</>
						:
						null
					}
				</TableBody>
			</Table>
		</TableContainer>
	)
})

const StatsCity = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	function cityTabChange(e, value) {
		gStorage.stats.setCityTab(value);
		gStorage.stats.setObsList(null);
	}

	function statsCityYearChange(event) {
		gStorage.stats.setStatsYear(event.target.value);
		gStorage.stats.setObsList(null);
	};
	

	if (gStorage.stats.tab !== TAB_LOCATION || gStorage.stats.loc_tab !== TAB_CITY)
		return null;

	return (
		<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
			<Grid size={{xs: 12}} align={'center'} id='cityfilter'>
				<StatsFilter 
					loc = {{country: {
							value: gStorage.stats.country,
							style: {width: 200}
						}}
					}
					time = {{
						year: {
							year: gStorage.stats.statsYear,
							onChange: statsCityYearChange,
							enabled: gStorage.conn.online,
							allowNone: true
						}}
					}
				/>
			</Grid>
			
			<Grid size={{xs: 12}} align='center' >
				<Tabs style={{width: 300}}
					id='citytab'
					value={gStorage.stats.city_tab}
					onChange={cityTabChange} 
					align={'center'}
					indicatorColor="primary"
					textColor="primary"
					variant="scrollable">
					<Tab
						value={CT_LIST}
						label={t('stats.city_list')}
					/>
					<Tab
						value={CT_MAP}
						label={t('stats.city_map')}
					/>
					<Tab
						value={CT_BY_MONTH}
						label={t('stats.city_by_month')}
					/>
				</Tabs>
			</Grid>

			<StatsCityList />
			<StatsCityByMonth />
			<StatsCityMap />
		</Grid>
	);
});

export default StatsCity;
